import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api';
import RefAdminConfig from './RefAdminConfig';
import './RefAdmin.css';

const SECTION_TITLES = {
  dashboard: ['Vue d\'ensemble', 'Données en temps réel · Mise à jour il y a 2 min'],
  membres: ['Gestion des membres', '247 membres · 14 en attente'],
  paiements: ['Paiements & Transactions', 'Historique complet'],
  evenements: ['Événements', '3 événements à venir'],
  compta: ['Comptabilité', 'Exercice 2024 · Données Stripe'],
  relances: ['Relances automatiques', 'Agent IA actif'],
  config: ['Configuration', 'Personnalisez votre espace'],
};

const INITIAL_RELANCES = [
  { id: 'j30', title: 'Rappel J-30 avant expiration', sub: 'Email envoyé automatiquement 30 jours avant la date d\'expiration', icon: '📧', iconBg: 'var(--brand-soft)', on: true },
  { id: 'j15', title: 'Rappel J-15 avant expiration', sub: 'Deuxième rappel avec lien de renouvellement direct (1x, 2x ou 3x)', icon: '📧', iconBg: 'rgba(255,173,77,0.1)', on: true },
  { id: 'j7', title: 'Rappel J-7 avant expiration', sub: 'Dernier rappel urgent — ton adapté par l\'agent IA', icon: '📧', iconBg: 'rgba(255,92,106,0.1)', on: true },
  { id: 'expired', title: 'Email cotisation expirée J+1', sub: 'Email automatique le lendemain de l\'expiration + passage en statut expiré', icon: '📧', iconBg: 'rgba(255,92,106,0.1)', on: true },
  { id: 'monthly', title: 'Relance mensuelle après expiration', sub: 'Email mensuel pendant 3 mois pour tenter un renouvellement tardif', icon: '📧', iconBg: 'rgba(107,143,136,0.1)', on: false },
  { id: 'demission', title: 'Courrier de démission avec signature', sub: 'Si un membre quitte, génère un courrier PDF signé électroniquement et archivé', icon: '🖊️', iconBg: 'var(--brand-soft)', on: true },
];

const INITIAL_PENDING_MEMBER = { id: 'pd', name: 'Pierre Durand', email: 'p.durand@archi.fr · Architecte', initials: 'PD' };

export default function RefAdmin({ live = false }) {
  const rootRef = useRef(null);
  const fileInputRef = useRef(null);

  const [section, setSection] = useState('dashboard');
  const [tab, setTab] = useState('identite');

  // Branding state
  const [brand, setBrand] = useState('#4DFFD2');
  const [nom, setNom] = useState("REF — Réseau d'Entrepreneurs Fugakwa");
  const [slogan, setSlogan] = useState('Construisons ensemble un réseau puissant');
  const [desc, setDesc] = useState("Le REF connecte les entrepreneurs, dirigeants et professionnels qui veulent échanger, collaborer et grandir ensemble.");

  // Counters
  const [totalMembres, setTotalMembres] = useState(247);
  const [pending, setPending] = useState(14);
  const [pendingMemberValidated, setPendingMemberValidated] = useState(false);

  // ── LIVE MODE ──
  const [livePending, setLivePending] = useState(false); // request in-flight
  const [liveMembres, setLiveMembres] = useState([]);    // tableau membres réel
  const [liveStats, setLiveStats] = useState(null);      // {total, actifs, en_attente, expires, revenus}
  const [liveAssoId, setLiveAssoId] = useState(null);
  const [liveAssoSlug, setLiveAssoSlug] = useState(null);
  const [importBusy, setImportBusy] = useState(false);

  // Relances
  const [relances, setRelances] = useState(INITIAL_RELANCES);

  // Formule editor
  const [formuleEditorOpen, setFormuleEditorOpen] = useState(false);
  const [feNom, setFeNom] = useState('');
  const [feDesc, setFeDesc] = useState('');
  const [fePrix, setFePrix] = useState('');
  const [feFreq, setFeFreq] = useState('annuel');

  // Toast
  const [toastMsg, setToastMsg] = useState('Modifications sauvegardées');
  const [toastShown, setToastShown] = useState(false);
  const toastTimer = useRef(null);

  // Filtre Membres (visuel uniquement, le tableau ne change pas réellement)
  const [memberFilter, setMemberFilter] = useState('all');

  // ── Mobile drawer ──
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);

  // Charger Google Fonts une seule fois
  useEffect(() => {
    const id = 'refadmin-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@700&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  // Couleurs dynamiques scopées au root du composant
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !/^#[0-9A-Fa-f]{6}$/.test(brand)) return;
    const r = parseInt(brand.slice(1, 3), 16);
    const g = parseInt(brand.slice(3, 5), 16);
    const b = parseInt(brand.slice(5, 7), 16);
    root.style.setProperty('--brand', brand);
    root.style.setProperty('--brand-soft', `rgba(${r},${g},${b},0.10)`);
    root.style.setProperty('--brand-border', `rgba(${r},${g},${b},0.22)`);
  }, [brand]);

  // Live counter simulation : nouveau membre toutes les ~12s (5% de chance) — uniquement en mode démo
  useEffect(() => {
    if (live) return undefined;
    const interval = setInterval(() => {
      if (Math.random() > 0.85) {
        setTotalMembres((n) => n + 1);
        showToast('🎉 Nouveau membre vient de rejoindre !');
      }
    }, 12000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);

  // ── LIVE MODE : charger asso + membres + stats au mount ──
  const reloadLive = async () => {
    try {
      const [{ data: asso }, { data: membres }, { data: stats }] = await Promise.all([
        api.get('/assos/me'),
        api.get('/membres'),
        api.get('/membres/stats'),
      ]);
      setNom(asso.nom || '');
      if (asso.couleur_principale) setBrand(asso.couleur_principale);
      if (asso.email_contact) {/* could populate */}
      setLiveAssoId(asso.id);
      setLiveAssoSlug(asso.slug);
      setLiveMembres(membres);
      setLiveStats(stats);
      setTotalMembres(stats.total || 0);
      setPending(stats.en_attente || 0);
    } catch (err) {
      console.error('reloadLive error', err);
      showToast('Erreur de chargement des données live');
    }
  };

  useEffect(() => {
    if (!live) return;
    reloadLive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);

  // Cleanup toast timer
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const showToast = (msg) => {
    setToastMsg(msg);
    setToastShown(true);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastShown(false), 3000);
  };

  const switchSection = (name) => setSection(name);

  const switchToConfigTab = (t) => {
    setTab(t);
    setSection('config');
  };

  const shortName = (() => {
    const base = nom.split('—')[0].trim().split(' ').filter(Boolean);
    const initials = base.map((w) => w[0]).join('').slice(0, 3);
    return initials || 'REF';
  })();

  const validerMembre = async (userId) => {
    if (live && userId) {
      if (livePending) return;
      setLivePending(true);
      try {
        await api.patch(`/membres/${userId}/valider`);
        showToast('Membre validé ! Email de bienvenue envoyé automatiquement ✅');
        await reloadLive();
      } catch (err) {
        console.error(err);
        showToast(err?.response?.data?.error || 'Erreur validation membre');
      } finally {
        setLivePending(false);
      }
      return;
    }
    if (pendingMemberValidated) return;
    setPendingMemberValidated(true);
    setPending((p) => Math.max(0, p - 1));
    setTotalMembres((n) => n + 1);
    showToast('Membre validé ! Email de bienvenue envoyé automatiquement ✅');
  };

  // ── LIVE : sauvegarder la config asso ──
  const saveConfig = async () => {
    if (!live) {
      showToast('✅ Configuration sauvegardée — Site public mis à jour en direct !');
      return;
    }
    setLivePending(true);
    try {
      await api.put('/assos/me', { nom, couleur_principale: brand });
      showToast('✅ Configuration sauvegardée — Site public mis à jour en direct !');
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.error || 'Erreur sauvegarde');
    } finally {
      setLivePending(false);
    }
  };

  // ── LIVE : import CSV ──
  const triggerImport = () => fileInputRef.current?.click();
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!live) {
      showToast('Mode démo — branchez-vous pour importer un CSV réel');
      return;
    }
    setImportBusy(true);
    try {
      const text = await file.text();
      const { data } = await api.post('/membres/import', { csv: text });
      const { created, skipped, errors } = data;
      let msg = `✅ ${created} membre(s) importé(s)`;
      if (skipped) msg += ` · ${skipped} déjà présent(s)`;
      if (errors?.length) msg += ` · ${errors.length} erreur(s)`;
      showToast(msg);
      await reloadLive();
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.error || 'Erreur import CSV');
    } finally {
      setImportBusy(false);
    }
  };

  // ── LIVE : inviter membre (modal simple via prompt) ──
  const inviterMembre = async () => {
    if (!live) {
      showToast('Invitation envoyée !');
      return;
    }
    // eslint-disable-next-line no-alert
    const email = window.prompt('Email du nouveau membre :');
    if (!email) return;
    // eslint-disable-next-line no-alert
    const prenom = window.prompt('Prénom :') || '';
    // eslint-disable-next-line no-alert
    const nomMembre = window.prompt('Nom :') || '';
    setLivePending(true);
    try {
      await api.post('/membres', { email, prenom, nom: nomMembre, formule: 'annuel' });
      showToast(`✅ ${email} ajouté à l'association`);
      await reloadLive();
    } catch (err) {
      console.error(err);
      showToast(err?.response?.data?.error || 'Erreur invitation');
    } finally {
      setLivePending(false);
    }
  };

  const toggleRelance = (id) => {
    setRelances((rs) => rs.map((r) => (r.id === id ? { ...r, on: !r.on } : r)));
  };

  const editFormule = (name, price) => {
    setFormuleEditorOpen(true);
    setFeNom(name);
    setFePrix(String(price).replace('€', ''));
    setFeDesc('');
    setTimeout(() => {
      document.getElementById('refadmin-formule-editor')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  const addFormule = () => {
    setFormuleEditorOpen(true);
    setFeNom('');
    setFePrix('');
    setFeDesc('');
    setTimeout(() => {
      document.getElementById('refadmin-formule-editor')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  };

  const fePreviewPrice = fePrix ? `${fePrix}€` : '30€';
  const fePreviewName = feNom || 'Adhésion individuelle';
  const fePreviewPeriod = ({ mensuel: 'par mois', trimestriel: 'par trimestre', semestriel: 'par semestre', annuel: 'par an', ponctuel: 'paiement unique' })[feFreq] || 'par an';

  const sbItem = (key, content, onClick, badge) => (
    <div
      className={'sb-item' + (
        (section === key) ||
        (key === 'config-identite' && section === 'config' && tab === 'identite') ||
        (key === 'config-stripe' && section === 'config' && tab === 'stripe') ||
        (key === 'config-formules' && section === 'config' && tab === 'formules') ||
        (key === 'config-sitePublic' && section === 'config' && tab === 'sitePublic') ||
        (key === 'config-relancesConfig' && section === 'config' && tab === 'relancesConfig')
          ? ' active' : '')}
      onClick={onClick}
      data-testid={`sb-${key}`}
    >
      {content}
      {badge && <span className="sb-badge">{badge}</span>}
    </div>
  );

  const titles = SECTION_TITLES[section] || ['—', ''];

  return (
    <div className={'refadmin-root' + (drawerOpen ? ' drawer-open' : '')} ref={rootRef} data-testid="refadmin-root">

      {/* Mobile hamburger */}
      <button className="mobile-menu-btn" onClick={() => setDrawerOpen((v) => !v)} aria-label={drawerOpen ? 'Fermer le menu' : 'Ouvrir le menu'} data-testid="mobile-menu-btn">
        {drawerOpen ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        )}
      </button>

      {/* Backdrop pour fermer le drawer sur mobile */}
      <div className="sidebar-backdrop" onClick={closeDrawer} />

      {/* TOAST */}
      <div className={'toast' + (toastShown ? ' show' : '')} data-testid="toast">
        ✅ <span>{toastMsg}</span>
      </div>

      {/* SIDEBAR */}
      <aside className="sidebar" onClick={(e) => {
        // Si on clique sur un nav-item, ferme le drawer (mobile only)
        if (e.target.closest('.nav-item, .sb-config-item')) closeDrawer();
      }}>
        <div className="sb-header">
          <div className="sb-logo">{shortName === 'REF' ? 'Ref' : shortName}</div>
          <div className="sb-assotype">Back-office Admin</div>
        </div>

        <div className="sb-asso-selector">
          <div className="sb-av">{shortName[0] || 'R'}</div>
          <div>
            <div className="sb-aname">{nom.split('—')[0].trim() || 'REF'}</div>
            <div className="sb-arole">Administrateur</div>
          </div>
        </div>

        <nav className="sb-nav">
          <div className="sb-section">Tableau de bord</div>
          {sbItem('dashboard', <><span className="sb-icon">⊞</span> Vue d'ensemble</>, () => switchSection('dashboard'))}
          {sbItem('membres', <><span className="sb-icon">👥</span> Membres</>, () => switchSection('membres'), totalMembres)}
          {sbItem('paiements', <><span className="sb-icon">💳</span> Paiements</>, () => switchSection('paiements'))}
          {sbItem('evenements', <><span className="sb-icon">📅</span> Événements</>, () => switchSection('evenements'))}
          {sbItem('compta', <><span className="sb-icon">📊</span> Comptabilité</>, () => switchSection('compta'))}
          {sbItem('relances', <><span className="sb-icon">🔔</span> Relances auto</>, () => switchSection('relances'))}

          <div className="sb-section">Configuration</div>
          {sbItem('config-identite', <><span className="sb-icon">🎨</span> Identité & Marque</>, () => switchToConfigTab('identite'))}
          {sbItem('config-stripe', <><span className="sb-icon">💳</span> Stripe Connect</>, () => switchToConfigTab('stripe'))}
          {sbItem('config-formules', <><span className="sb-icon">📋</span> Formules & Tarifs</>, () => switchToConfigTab('formules'))}
          {sbItem('config-sitePublic', <><span className="sb-icon">🌐</span> Site public</>, () => switchToConfigTab('sitePublic'))}
          {sbItem('config-relancesConfig', <><span className="sb-icon">⚙️</span> Paramètres</>, () => switchToConfigTab('relancesConfig'))}
        </nav>

        <div className="sb-footer">
          <div className="sb-user">
            <div className="sb-uav">MD</div>
            <div>
              <div className="sb-uname">Marie Dupont</div>
              <div className="sb-uemail">admin@ref.fr</div>
            </div>
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <div className="main">

        {/* TOPBAR */}
        <div className="topbar">
          <div className="topbar-left">
            <div>
              <div className="topbar-title" data-testid="topbar-title">{titles[0]}</div>
              <div className="topbar-sub">{titles[1]}</div>
            </div>
          </div>
          <div className="topbar-actions">
            <button className="btn btn-ghost" onClick={() => showToast('Données actualisées !')} data-testid="btn-refresh">🔄 Actualiser</button>
            <button className="btn btn-primary" onClick={() => switchSection('config')} data-testid="btn-configurer">⚙️ Configurer</button>
          </div>
        </div>

        {/* CONFIG TABS */}
        {section === 'config' && (
          <div className="config-tabs" data-testid="config-tabs">
            <button className={'ctab' + (tab === 'identite' ? ' active' : '')} onClick={() => setTab('identite')} data-testid="ctab-identite">🎨 Identité</button>
            <button className={'ctab' + (tab === 'stripe' ? ' active' : '')} onClick={() => setTab('stripe')} data-testid="ctab-stripe">💳 Stripe</button>
            <button className={'ctab' + (tab === 'formules' ? ' active' : '')} onClick={() => setTab('formules')} data-testid="ctab-formules">📋 Formules</button>
            <button className={'ctab' + (tab === 'sitePublic' ? ' active' : '')} onClick={() => setTab('sitePublic')} data-testid="ctab-sitePublic">🌐 Site public</button>
            <button className={'ctab' + (tab === 'relancesConfig' ? ' active' : '')} onClick={() => setTab('relancesConfig')} data-testid="ctab-relancesConfig">⚙️ Paramètres</button>
          </div>
        )}

        <div className="content">

          {/* ══════════════ DASHBOARD ══════════════ */}
          {section === 'dashboard' && (
            <div data-testid="section-dashboard">
              <div className="live-grid">
                <div className="live-card">
                  <div className="live-dot"></div>
                  <div className="live-val">{live && liveStats ? liveStats.actifs : totalMembres}</div>
                  <div className="live-lbl">Membres actifs</div>
                </div>
                <div className="live-card">
                  <div className="live-val" style={{ color: 'var(--green)' }}>
                    {live && liveStats ? `${Number(liveStats.revenus).toFixed(0)}€` : '7 420€'}
                  </div>
                  <div className="live-lbl">Cotisations {live ? 'année en cours' : '2024'}</div>
                </div>
                <div className="live-card">
                  <div className="live-val" style={{ color: 'var(--orange)' }}>
                    {live && liveStats ? liveStats.en_attente : pending}
                  </div>
                  <div className="live-lbl">En attente validation</div>
                </div>
                <div className="live-card">
                  <div className="live-val" style={{ color: 'var(--red)' }}>
                    {live && liveStats ? liveStats.expires : 8}
                  </div>
                  <div className="live-lbl">Cotisations expirées</div>
                </div>
              </div>

              <div className="grid-2">
                <div className="card">
                  <div className="card-header">
                    <div className="card-title">👥 Derniers membres</div>
                    <button className="btn btn-ghost" style={{ fontSize: '11px', padding: '5px 10px' }} onClick={() => switchSection('membres')}>Voir tous</button>
                  </div>
                  <div className="card-body" style={{ padding: 0 }}>
                    <table>
                      <thead><tr><th>Membre</th><th>Formule</th><th>Statut</th><th>Actions</th></tr></thead>
                      <tbody>
                        {live ? (
                          liveMembres.slice(0, 5).map((m) => {
                            const initials = ((m.prenom || '?')[0] + (m.nom || '?')[0]).toUpperCase();
                            const isExpired = m.date_expiration && new Date(m.date_expiration) < new Date();
                            const statut = isExpired ? 'expired' : (m.statut || 'actif');
                            const statutLabel = statut === 'expired' ? 'Expiré' : statut === 'en_attente' ? 'En attente' : statut === 'actif' ? 'Actif' : statut;
                            const chipCls = statut === 'expired' ? 'sc-expired' : statut === 'en_attente' ? 'sc-pending' : 'sc-active';
                            const avBg = statut === 'expired' ? 'var(--red)' : statut === 'en_attente' ? 'var(--orange)' : 'var(--brand)';
                            const avColor = statut === 'en_attente' ? '#080c0b' : (statut === 'expired' ? 'white' : '#080c0b');
                            return (
                              <tr key={m.id}>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                                    <div className="m-av-sm" style={{ background: avBg, color: avColor }}>{initials}</div>
                                    <div>
                                      <div style={{ fontWeight: 600 }}>{m.prenom} {m.nom}</div>
                                      <div style={{ fontSize: '11px', color: 'var(--muted)' }}>{m.email}</div>
                                    </div>
                                  </div>
                                </td>
                                <td style={{ fontSize: '12px' }}>{m.formule || '—'}</td>
                                <td><span className={'status-chip ' + chipCls}>{statutLabel}</span></td>
                                <td>
                                  {statut === 'en_attente' ? (
                                    <button className="btn btn-primary" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => validerMembre(m.id)} disabled={livePending} data-testid={`btn-valider-${m.id}`}>✓ Valider</button>
                                  ) : (
                                    <button className="btn btn-ghost" style={{ fontSize: '11px', padding: '4px 8px' }}>Voir</button>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <>
                            <tr>
                              <td><div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}><div className="m-av-sm" style={{ background: 'var(--brand)' }}>JM</div><div><div style={{ fontWeight: 600 }}>Jean Martin</div><div style={{ fontSize: '11px', color: 'var(--muted)' }}>jean@martin.fr</div></div></div></td>
                              <td style={{ fontSize: '12px' }}>Individuelle · Annuel</td>
                              <td><span className="status-chip sc-active">Actif</span></td>
                              <td><button className="btn btn-ghost" style={{ fontSize: '11px', padding: '4px 8px' }}>Voir</button></td>
                            </tr>
                            <tr>
                              <td><div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}><div className="m-av-sm" style={{ background: '#7c5cfc', color: 'white' }}>SB</div><div><div style={{ fontWeight: 600 }}>Sophie Bernard</div><div style={{ fontSize: '11px', color: 'var(--muted)' }}>sophie@rh.fr</div></div></div></td>
                              <td style={{ fontSize: '12px' }}>Duo · Annuel</td>
                              <td><span className="status-chip sc-active">Actif</span></td>
                              <td><button className="btn btn-ghost" style={{ fontSize: '11px', padding: '4px 8px' }}>Voir</button></td>
                            </tr>
                            <tr>
                              <td><div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}><div className="m-av-sm" style={{ background: 'var(--orange)', color: '#080c0b' }}>PD</div><div><div style={{ fontWeight: 600 }}>Pierre Durand</div><div style={{ fontSize: '11px', color: 'var(--muted)' }}>p.durand@archi.fr</div></div></div></td>
                              <td style={{ fontSize: '12px' }}>Individuelle · Annuel</td>
                              <td>
                                <span className={'status-chip ' + (pendingMemberValidated ? 'sc-active' : 'sc-pending')}>
                                  {pendingMemberValidated ? 'Actif' : 'En attente'}
                                </span>
                              </td>
                              <td>
                                {pendingMemberValidated ? (
                                  <button className="btn btn-ghost" style={{ fontSize: '11px', padding: '4px 8px' }}>Voir</button>
                                ) : (
                                  <div style={{ display: 'flex', gap: '4px' }}>
                                    <button className="btn btn-primary" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => validerMembre()} data-testid="btn-valider-membre">✓ Valider</button>
                                    <button className="btn btn-ghost" style={{ fontSize: '11px', padding: '4px 8px' }}>Voir</button>
                                  </div>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td><div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}><div className="m-av-sm" style={{ background: 'var(--red)', color: 'white' }}>TM</div><div><div style={{ fontWeight: 600 }}>Thomas Moreau</div><div style={{ fontSize: '11px', color: 'var(--muted)' }}>t.moreau@gmail.com</div></div></div></td>
                              <td style={{ fontSize: '12px' }}>Individuelle · Annuel</td>
                              <td><span className="status-chip sc-expired">Expiré</span></td>
                              <td><button className="btn btn-ghost" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => showToast('Email de relance envoyé !')}>Relancer</button></td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header"><div className="card-title">📊 Revenus mensuels</div></div>
                  <div className="card-body">
                    <div className="bar-chart">
                      {[45, 60, 35, 75, 55, 90, 40].map((h, i) => (
                        <div className="bar-col" key={i}>
                          <div className="bar-fill" style={{ height: `${h}%` }}></div>
                          <div className="bar-month">{['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul'][i]}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' }}><span style={{ color: 'var(--muted)' }}>Ce mois</span><span style={{ fontWeight: 700, color: 'var(--green)' }}>+1 240€</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '13px' }}><span style={{ color: 'var(--muted)' }}>Mois dernier</span><span style={{ fontWeight: 600 }}>1 080€</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '13px' }}><span style={{ color: 'var(--muted)' }}>Total 2024</span><span style={{ fontWeight: 800, color: 'var(--brand)' }}>7 420€</span></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="card-title">🔔 Relances automatiques en cours</div>
                  <span style={{ fontSize: '12px', color: 'var(--orange)' }}>8 cotisations expirées · 14 expirent bientôt</span>
                </div>
                <div className="card-body">
                  <div className="relance-item">
                    <div className="ri-icon" style={{ background: 'rgba(255,92,106,0.1)' }}>📧</div>
                    <div className="ri-info"><div className="ri-title">Thomas Moreau — Cotisation expirée J+30</div><div className="ri-sub">Relance envoyée automatiquement · 3ème email · En attente de réponse</div></div>
                    <span className="status-chip sc-expired">J+30</span>
                    <button className="btn btn-ghost" style={{ fontSize: '11px', padding: '5px 9px' }}>Voir</button>
                  </div>
                  <div className="relance-item">
                    <div className="ri-icon" style={{ background: 'rgba(255,173,77,0.1)' }}>📧</div>
                    <div className="ri-info"><div className="ri-title">Alice Lambert — Expiration dans 15 jours</div><div className="ri-sub">Email de rappel envoyé automatiquement hier · Lien de renouvellement inclus</div></div>
                    <span className="status-chip sc-pending">J-15</span>
                    <button className="btn btn-ghost" style={{ fontSize: '11px', padding: '5px 9px' }}>Voir</button>
                  </div>
                  <div className="relance-item">
                    <div className="ri-icon" style={{ background: 'rgba(255,173,77,0.1)' }}>📧</div>
                    <div className="ri-info"><div className="ri-title">Marc Leblanc — Expiration dans 7 jours</div><div className="ri-sub">Dernier rappel planifié · Paiement en 1, 2 ou 3 fois proposé</div></div>
                    <span className="status-chip sc-pending">J-7</span>
                    <button className="btn btn-ghost" style={{ fontSize: '11px', padding: '5px 9px' }}>Voir</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════ MEMBRES ══════════════ */}
          {section === 'membres' && (
            <div data-testid="section-membres">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {(live && liveStats ? [
                    ['all', `Tous (${liveStats.total})`],
                    ['active', `Actifs (${liveStats.actifs})`],
                    ['pending', `En attente (${liveStats.en_attente})`],
                    ['expired', `Expirés (${liveStats.expires})`],
                  ] : [
                    ['all', 'Tous (247)'], ['active', 'Actifs (225)'], ['pending', 'En attente (14)'], ['expired', 'Expirés (8)'],
                  ]).map(([f, label]) => (
                    <button
                      key={f}
                      className="btn btn-ghost"
                      style={{ fontSize: '12px', ...(memberFilter === f ? { borderColor: 'var(--brand)', color: 'var(--brand)' } : {}) }}
                      onClick={() => setMemberFilter(f)}
                      data-testid={`member-filter-${f}`}
                    >{label}</button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input ref={fileInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleFileChange} data-testid="csv-input" />
                  <button className="btn btn-ghost" onClick={triggerImport} disabled={importBusy} data-testid="btn-import-csv">
                    {importBusy ? '⏳ Import...' : '📤 Importer CSV'}
                  </button>
                  <button className="btn btn-primary" onClick={inviterMembre} disabled={livePending} data-testid="btn-inviter-membre">＋ Inviter un membre</button>
                </div>
              </div>
              <div className="card">
                <div className="card-body" style={{ padding: 0 }}>
                  <div className="table-wrap">
                    <table>
                      <thead><tr><th>Membre</th><th>Formule</th><th>Date adhésion</th><th>Expiration</th><th>Statut</th><th>Actions</th></tr></thead>
                      <tbody>
                        {live ? (
                          (() => {
                            const filtered = liveMembres.filter((m) => {
                              const isExpired = m.date_expiration && new Date(m.date_expiration) < new Date();
                              if (memberFilter === 'all') return true;
                              if (memberFilter === 'active') return m.statut === 'actif' && !isExpired;
                              if (memberFilter === 'pending') return m.statut === 'en_attente';
                              if (memberFilter === 'expired') return isExpired;
                              return true;
                            });
                            if (!filtered.length) {
                              return (
                                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)', fontSize: '13px' }}>
                                  Aucun membre dans cette catégorie
                                </td></tr>
                              );
                            }
                            return filtered.map((m) => {
                              const initials = ((m.prenom || '?')[0] + (m.nom || '?')[0]).toUpperCase();
                              const isExpired = m.date_expiration && new Date(m.date_expiration) < new Date();
                              const statut = isExpired ? 'expired' : (m.statut || 'actif');
                              const statutLabel = statut === 'expired' ? 'Expiré' : statut === 'en_attente' ? 'En attente' : 'Actif';
                              const chipCls = statut === 'expired' ? 'sc-expired' : statut === 'en_attente' ? 'sc-pending' : 'sc-active';
                              const avBg = statut === 'expired' ? 'var(--red)' : statut === 'en_attente' ? 'var(--orange)' : 'var(--brand)';
                              const avColor = statut === 'expired' ? 'white' : '#080c0b';
                              const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                              return (
                                <tr key={m.id}>
                                  <td><div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                                    <div className="m-av-sm" style={{ background: avBg, color: avColor }}>{initials}</div>
                                    <div><div style={{ fontWeight: 600 }}>{m.prenom} {m.nom}</div>
                                    <div style={{ fontSize: '11px', color: 'var(--muted)' }}>{m.email}</div></div>
                                  </div></td>
                                  <td style={{ fontSize: '12px' }}>{m.formule || '—'}</td>
                                  <td style={{ fontSize: '12px', color: 'var(--muted)' }}>{fmtDate(m.date_adhesion)}</td>
                                  <td style={{ fontSize: '12px', color: isExpired ? 'var(--red)' : 'var(--muted)' }}>{fmtDate(m.date_expiration)} {isExpired ? '⚠️' : ''}</td>
                                  <td><span className={'status-chip ' + chipCls}>{statutLabel}</span></td>
                                  <td>
                                    {statut === 'en_attente' ? (
                                      <div style={{ display: 'flex', gap: '4px' }}>
                                        <button className="btn btn-primary" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => validerMembre(m.id)} disabled={livePending} data-testid={`btn-valider-${m.id}`}>✓ Valider</button>
                                      </div>
                                    ) : statut === 'expired' ? (
                                      <button className="btn btn-ghost" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => showToast('Email de relance envoyé !')}>📧 Relancer</button>
                                    ) : (
                                      <button className="btn btn-ghost" style={{ fontSize: '11px', padding: '4px 8px' }}>Voir</button>
                                    )}
                                  </td>
                                </tr>
                              );
                            });
                          })()
                        ) : (
                          <>
                            <tr>
                              <td><div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}><div className="m-av-sm" style={{ background: 'var(--brand)' }}>JM</div><div><div style={{ fontWeight: 600 }}>Jean Martin</div><div style={{ fontSize: '11px', color: 'var(--muted)' }}>jean@martin.fr · CEO</div></div></div></td>
                              <td style={{ fontSize: '12px' }}>Individuelle</td>
                              <td style={{ fontSize: '12px', color: 'var(--muted)' }}>12 jan. 2024</td>
                              <td style={{ fontSize: '12px', color: 'var(--muted)' }}>31 déc. 2024</td>
                              <td><span className="status-chip sc-active">Actif</span></td>
                              <td><button className="btn btn-ghost" style={{ fontSize: '11px', padding: '4px 8px' }}>Voir</button></td>
                            </tr>
                            <tr>
                              <td><div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}><div className="m-av-sm" style={{ background: '#7c5cfc', color: 'white' }}>SB</div><div><div style={{ fontWeight: 600 }}>Sophie Bernard</div><div style={{ fontSize: '11px', color: 'var(--muted)' }}>sophie@rh.fr · Consultante</div></div></div></td>
                              <td style={{ fontSize: '12px' }}>Duo</td>
                              <td style={{ fontSize: '12px', color: 'var(--muted)' }}>15 jan. 2024</td>
                              <td style={{ fontSize: '12px', color: 'var(--muted)' }}>31 déc. 2024</td>
                              <td><span className="status-chip sc-active">Actif</span></td>
                              <td><button className="btn btn-ghost" style={{ fontSize: '11px', padding: '4px 8px' }}>Voir</button></td>
                            </tr>
                            <tr>
                              <td><div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}><div className="m-av-sm" style={{ background: 'var(--orange)', color: '#080c0b' }}>PD</div><div><div style={{ fontWeight: 600 }}>Pierre Durand</div><div style={{ fontSize: '11px', color: 'var(--muted)' }}>p.durand@archi.fr · Architecte</div></div></div></td>
                              <td style={{ fontSize: '12px' }}>Individuelle</td>
                              <td style={{ fontSize: '12px', color: 'var(--muted)' }}>En attente</td>
                              <td style={{ fontSize: '12px', color: 'var(--muted)' }}>—</td>
                              <td><span className="status-chip sc-pending">En attente</span></td>
                              <td><div style={{ display: 'flex', gap: '4px' }}><button className="btn btn-primary" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => validerMembre()}>✓ Valider</button><button className="btn btn-danger" style={{ fontSize: '11px', padding: '4px 8px' }}>✗</button></div></td>
                            </tr>
                            <tr>
                              <td><div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}><div className="m-av-sm" style={{ background: 'var(--red)', color: 'white' }}>TM</div><div><div style={{ fontWeight: 600 }}>Thomas Moreau</div><div style={{ fontSize: '11px', color: 'var(--muted)' }}>t.moreau@gmail.com</div></div></div></td>
                              <td style={{ fontSize: '12px' }}>Individuelle</td>
                              <td style={{ fontSize: '12px', color: 'var(--muted)' }}>8 fév. 2023</td>
                              <td style={{ fontSize: '12px', color: 'var(--red)' }}>31 déc. 2023 ⚠️</td>
                              <td><span className="status-chip sc-expired">Expiré</span></td>
                              <td><button className="btn btn-ghost" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => showToast('Email de relance envoyé !')}>📧 Relancer</button></td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════ COMPTA ══════════════ */}
          {section === 'compta' && (
            <div data-testid="section-compta">
              <div className="compta-grid">
                <div className="compta-card"><div className="cc-label">Revenus totaux 2024</div><div className="cc-val" style={{ color: 'var(--green)' }}>7 420€</div><div className="cc-trend trend-up">↑ +12% vs 2023</div></div>
                <div className="compta-card"><div className="cc-label">Dépenses totales 2024</div><div className="cc-val" style={{ color: 'var(--red)' }}>3 180€</div><div className="cc-trend trend-down">↑ +5% vs 2023</div></div>
                <div className="compta-card"><div className="cc-label">Solde disponible</div><div className="cc-val" style={{ color: 'var(--brand)' }}>4 240€</div><div className="cc-trend" style={{ color: 'var(--muted)' }}>Exercice 2024</div></div>
                <div className="compta-card"><div className="cc-label">Cotisations en attente</div><div className="cc-val" style={{ color: 'var(--orange)' }}>640€</div><div className="cc-trend" style={{ color: 'var(--muted)' }}>8 membres expirés</div></div>
              </div>

              <div className="grid-2">
                <div className="card">
                  <div className="card-header"><div className="card-title">💰 Dernières transactions</div><button className="btn btn-ghost" style={{ fontSize: '11px', padding: '5px 10px' }}>Exporter CSV</button></div>
                  <div className="card-body" style={{ padding: 0 }}>
                    <table>
                      <thead><tr><th>Membre</th><th>Type</th><th>Montant</th><th>Date</th></tr></thead>
                      <tbody>
                        <tr><td style={{ fontSize: '12px', fontWeight: 600 }}>Jean Martin</td><td style={{ fontSize: '11px', color: 'var(--muted)' }}>Cotisation annuelle</td><td style={{ fontWeight: 700, color: 'var(--green)' }}>+30€</td><td style={{ fontSize: '11px', color: 'var(--muted)' }}>12 jan.</td></tr>
                        <tr><td style={{ fontSize: '12px', fontWeight: 600 }}>Location salle</td><td style={{ fontSize: '11px', color: 'var(--muted)' }}>Dépense événement</td><td style={{ fontWeight: 700, color: 'var(--red)' }}>-250€</td><td style={{ fontSize: '11px', color: 'var(--muted)' }}>10 jan.</td></tr>
                        <tr><td style={{ fontSize: '12px', fontWeight: 600 }}>Sophie Bernard</td><td style={{ fontSize: '11px', color: 'var(--muted)' }}>Cotisation duo</td><td style={{ fontWeight: 700, color: 'var(--green)' }}>+50€</td><td style={{ fontSize: '11px', color: 'var(--muted)' }}>15 jan.</td></tr>
                        <tr><td style={{ fontSize: '12px', fontWeight: 600 }}>Traiteur soirée</td><td style={{ fontSize: '11px', color: 'var(--muted)' }}>Dépense événement</td><td style={{ fontWeight: 700, color: 'var(--red)' }}>-180€</td><td style={{ fontSize: '11px', color: 'var(--muted)' }}>8 jan.</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="card">
                  <div className="card-header"><div className="card-title">📈 Répartition revenus</div></div>
                  <div className="card-body">
                    <div style={{ fontSize: '13px', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}><span style={{ color: 'var(--muted)' }}>Cotisations individuelles</span><span style={{ fontWeight: 700, color: 'var(--brand)' }}>5 100€ (69%)</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}><span style={{ color: 'var(--muted)' }}>Cotisations duo</span><span style={{ fontWeight: 700 }}>1 500€ (20%)</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}><span style={{ color: 'var(--muted)' }}>Cotisations entreprise</span><span style={{ fontWeight: 700 }}>640€ (9%)</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}><span style={{ color: 'var(--muted)' }}>Événements payants</span><span style={{ fontWeight: 700 }}>180€ (2%)</span></div>
                    </div>
                    <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center' }} onClick={() => showToast('Export PDF généré !')}>📄 Exporter rapport comptable</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════ RELANCES ══════════════ */}
          {section === 'relances' && (
            <div data-testid="section-relances">
              <div className="card">
                <div className="card-header"><div className="card-title">🤖 Relances automatiques</div><span style={{ fontSize: '12px', color: 'var(--green)' }}>Agent IA actif</span></div>
                <div className="card-body">
                  {relances.map((r) => (
                    <div className="relance-item" key={r.id}>
                      <div className="ri-icon" style={{ background: r.iconBg }}>{r.icon}</div>
                      <div className="ri-info"><div className="ri-title">{r.title}</div><div className="ri-sub">{r.sub}</div></div>
                      <div
                        className={'ri-toggle ' + (r.on ? 'on' : 'off')}
                        onClick={() => toggleRelance(r.id)}
                        data-testid={`relance-toggle-${r.id}`}
                      ></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ══════════════ PAIEMENTS ══════════════ */}
          {section === 'paiements' && (
            <div data-testid="section-paiements">
              <div className="card">
                <div className="card-header"><div className="card-title">💳 Historique des paiements</div><button className="btn btn-ghost" style={{ fontSize: '11px', padding: '5px 10px' }}>Exporter</button></div>
                <div className="card-body" style={{ padding: 0 }}>
                  <table>
                    <thead><tr><th>Membre</th><th>Formule</th><th>Montant</th><th>Versements</th><th>Statut</th><th>Date</th></tr></thead>
                    <tbody>
                      <tr><td style={{ fontWeight: 600 }}>Jean Martin</td><td style={{ fontSize: '12px' }}>Individuelle</td><td style={{ fontWeight: 700, color: 'var(--green)' }}>30€</td><td style={{ fontSize: '12px' }}>1×30€</td><td><span className="status-chip sc-active">Payé</span></td><td style={{ fontSize: '12px', color: 'var(--muted)' }}>12 jan. 2024</td></tr>
                      <tr><td style={{ fontWeight: 600 }}>Sophie Bernard</td><td style={{ fontSize: '12px' }}>Duo</td><td style={{ fontWeight: 700, color: 'var(--green)' }}>50€</td><td style={{ fontSize: '12px' }}>2×25€</td><td><span className="status-chip sc-active">Payé</span></td><td style={{ fontSize: '12px', color: 'var(--muted)' }}>15 jan. 2024</td></tr>
                      <tr><td style={{ fontWeight: 600 }}>Pierre Durand</td><td style={{ fontSize: '12px' }}>Individuelle</td><td style={{ fontWeight: 700, color: 'var(--orange)' }}>30€</td><td style={{ fontSize: '12px' }}>3×10€</td><td><span className="status-chip sc-pending">En cours</span></td><td style={{ fontSize: '12px', color: 'var(--muted)' }}>En attente</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════ ÉVÉNEMENTS ══════════════ */}
          {section === 'evenements' && (
            <div data-testid="section-evenements">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ fontSize: '14px', color: 'var(--muted)' }}>Gérez vos événements — inscriptions en temps réel</div>
                <button className="btn btn-primary" onClick={() => showToast('Créer un événement — page dédiée')}>＋ Créer un événement</button>
              </div>
              <div className="card"><div className="card-body" style={{ padding: 0 }}>
                <table>
                  <thead><tr><th>Événement</th><th>Date</th><th>Inscrits</th><th>Places</th><th>Statut</th><th>Actions</th></tr></thead>
                  <tbody>
                    <tr><td style={{ fontWeight: 600 }}>Petit-déjeuner Entrepreneurs</td><td style={{ fontSize: '12px' }}>15 juin 2024</td><td style={{ fontWeight: 700, color: 'var(--brand)' }}>32</td><td style={{ fontSize: '12px', color: 'var(--muted)' }}>/50</td><td><span className="status-chip sc-active">Ouvert</span></td><td><button className="btn btn-ghost" style={{ fontSize: '11px', padding: '4px 8px' }}>Gérer</button></td></tr>
                    <tr><td style={{ fontWeight: 600 }}>Soirée Networking Business</td><td style={{ fontSize: '12px' }}>22 juin 2024</td><td style={{ fontWeight: 700, color: 'var(--orange)' }}>78</td><td style={{ fontSize: '12px', color: 'var(--muted)' }}>/80</td><td><span className="status-chip sc-pending">Bientôt complet</span></td><td><button className="btn btn-ghost" style={{ fontSize: '11px', padding: '4px 8px' }}>Gérer</button></td></tr>
                    <tr><td style={{ fontWeight: 600 }}>Atelier Lever des fonds</td><td style={{ fontSize: '12px' }}>5 juil. 2024</td><td style={{ fontWeight: 700, color: 'var(--brand)' }}>14</td><td style={{ fontSize: '12px', color: 'var(--muted)' }}>/30</td><td><span className="status-chip sc-active">Ouvert</span></td><td><button className="btn btn-ghost" style={{ fontSize: '11px', padding: '4px 8px' }}>Gérer</button></td></tr>
                  </tbody>
                </table>
              </div></div>
            </div>
          )}

          {/* ══════════════ CONFIG ══════════════ */}
          {section === 'config' && (
            <RefAdminConfig
              tab={tab}
              nom={nom} setNom={setNom}
              slogan={slogan} setSlogan={setSlogan}
              desc={desc} setDesc={setDesc}
              brand={brand} setBrand={setBrand}
              shortName={shortName}
              totalMembres={totalMembres}
              showToast={showToast} saveConfig={saveConfig} livePending={livePending}
              editFormule={editFormule} addFormule={addFormule}
              formuleEditorOpen={formuleEditorOpen} setFormuleEditorOpen={setFormuleEditorOpen}
              feNom={feNom} setFeNom={setFeNom}
              feDesc={feDesc} setFeDesc={setFeDesc}
              fePrix={fePrix} setFePrix={setFePrix}
              feFreq={feFreq} setFeFreq={setFeFreq}
              fePreviewPrice={fePreviewPrice} fePreviewName={fePreviewName} fePreviewPeriod={fePreviewPeriod}
            />
          )}

        </div>{/* /content */}
      </div>{/* /main */}
    </div>
  );
}
